import { Action } from 'redux';
import {
	AppView,
	Lightbox,
	SofaRange,
	FromStage,
	ShoppingItem,
	ProductCardItem,
	YmalSectionType,
	YmalType,
} from 'sofa-reborn-react';
import { IProduct } from '@inter-ikea-kompis/types';

// State
export interface ApplicationState {
	appInitialized: boolean;
	appView: AppView;
	currentLightbox: Lightbox;
	defaultApplication?: string;
	hasFirstInteractionHappened: boolean;
	browserIsDetected: boolean;
	languageChanged: FromStage | null;
	coverChanged: Lightbox | null;
	loadingModels: boolean;
	loadingCovers: boolean;
	assetFolder: string | null;
	showDebug?: boolean;
	useIndividualAssets: boolean;
	hasVisitScene: boolean;
	showFakeDataForm?: boolean;
	fakeData: IProduct[] | null;
	showIpexGalleryButton: boolean;
	idleTime: number | null;
	isInUpptackaPlatform: boolean;
	footerHeight?: number;
	headerHeight?: number;
	productVariantsShowMore: boolean;
	disableBulkFetch: boolean;
	insightsInitiated: boolean;
	usePersistedConfig: boolean;
	totalPriceVoice: string | null;
}

// Actions
export const APP_INIT = 'APP_INIT';
export const SET_APP_VIEW = 'SET_APP_VIEW';
export const SET_CURRENT_LIGHTBOX = 'SET_CURRENT_LIGHTBOX';
export const SET_SOFA_RANGE = 'SET_SOFA_RANGE';
export const SET_BROWSER_IS_DETECTED = 'SET_BROWSER_IS_DETECTED';

export const INIT_IFRAME_HANDLER = 'INIT_IFRAME_HANDLER';
export const UPDATE_IFRAME_HEIGHT = 'UPDATE_IFRAME_HEIGHT';
export const RESET_IFRAME_HEIGHT = 'RESET_IFRAME_HEIGHT';

export const SET_DEFAULT_APPLICATION = 'SET_DEFAULT_APPLICATION';
export const SUCCESS_ADD_TO_BAG = 'SUCCESS_ADD_TO_BAG';
export const SUCCESS_ADD_TO_BAG_YMAL = 'SUCCESS_ADD_TO_BAG_YMAL';
export const DONE_SAVE_VPC = 'DONE_SAVE_VPC';
export const SWIPE_IN_MODULE_SWIPER = 'SWIPE_IN_MODULE_SWIPER';

export const SET_HAS_FIRST_INTERACTION_HAPPENED = 'SET_HAS_FIRST_INTERACTION_HAPPENED';
export const SET_LANGUAGE_CHANGED = 'SET_LANGUAGE_CHANGED';
export const SET_COVER_CHANGED = 'SET_COVER_CHANGED';
export const SET_LOADING_MODELS = 'SET_LOADING_MODELS';
export const SET_LOADING_COVERS = 'SET_LOADING_COVERS';
export const SET_IS_DEEPLINKED = 'SET_IS_DEEPLINKED';

export const SET_ASSET_FOLDER = 'SET_ASSET_FOLDER';
export const SHOW_DEBUG = 'SHOW_DEBUG';
export const SET_USE_INDIVIDUAL_ASSETS = 'SET_USE_INDIVIDUAL_ASSETS';
export const SET_HAS_VISIT_SCENE = 'SET_HAS_VISIT_SCENE';

export const SET_SHOW_FAKE_DATA_FORM = 'SET_SHOW_FAKE_DATA_FORM';
export const SET_FAKE_DATA = 'SET_FAKE_DATA';

export const SET_SHOW_IPEX_GALLERY_BUTTON = 'SET_SHOW_IPEX_GALLERY_BUTTON';
export const SET_IDLE_TIME = 'SET_IDLE_TIME';
export const SET_IS_IN_UPPTACKA_PLATFORM = 'SET_IS_IN_UPPTACKA_PLATFORM';
export const SET_FROM_NAVIGATOR = 'SET_FROM_NAVIGATOR';

export const SET_FOOTER_HEIGHT = 'SET_FOOTER_HEIGHT';
export const SET_HEADER_HEIGHT = 'SET_HEADER_HEIGHT';

export const SET_PRODUCT_VARIANTS_SHOW_MORE = 'SET_PRODUCT_VARIANTS_SHOW_MORE';
export const SET_PRODUCT_CARD_TYPE_CLICK = 'SET_PRODUCT_CARD_TYPE_CLICK';

export const SET_DISABLE_BULK_FETCH = 'SET_DISABLE_BULK_FETCH';
export const SET_INSIGHTS_INITIATED = 'SET_INSIGHTS_INITIATED';
export const SET_USE_PERSISTED_CONFIG = 'SET_USE_PERSISTED_CONFIG';
export const NO_ARTICLES_IN_PLANNER = 'NO_ARTICLES_IN_PLANNER';
export const THUMBNAIL_IMAGE_CHANGED = 'THUMBNAIL_IMAGE_CHANGED';
export const SET_TOTAL_PRICE_VOICE = 'SET_TOTAL_PRICE_VOICE';

export const STATISTICS_QR_PLANNING_ENABLED = 'STATISTICS_QR_PLANNING_ENABLED';

interface SetAppView extends Action {
	type: typeof SET_APP_VIEW;
	value: AppView;
}

interface SetCurrentLightbox extends Action {
	type: typeof SET_CURRENT_LIGHTBOX;
	value: Lightbox;
}

interface SetSofaRange extends Action {
	type: typeof SET_SOFA_RANGE;
	value: SofaRange;
}

interface AppInitAction extends Action {
	type: typeof APP_INIT;
}

interface InitIframeHandler extends Action {
	type: typeof INIT_IFRAME_HANDLER;
}

interface UpdateIframeHeight extends Action {
	type: typeof UPDATE_IFRAME_HEIGHT;
	value: number;
}

interface ResetIframeHeight extends Action {
	type: typeof RESET_IFRAME_HEIGHT;
}

interface SetDefaultApplication extends Action {
	type: typeof SET_DEFAULT_APPLICATION;
	value: string;
}

interface SuccessAddToBag extends Action {
	type: typeof SUCCESS_ADD_TO_BAG;
	value: CustomEvent;
	hasAdditionalCover: boolean;
}

interface SuccessAddToBagYmal extends Action {
	type: typeof SUCCESS_ADD_TO_BAG_YMAL;
	value: {
		shoppingItems: ShoppingItem[];
		type: YmalType;
		sectionType: YmalSectionType;
	};
}

interface DoneSaveVpc extends Action {
	type: typeof DONE_SAVE_VPC;
	value: string;
}

interface SwipeInModuleSwiper extends Action {
	type: typeof SWIPE_IN_MODULE_SWIPER;
}

interface SetHasFirstInteractionHappened extends Action {
	type: typeof SET_HAS_FIRST_INTERACTION_HAPPENED;
	value: boolean;
}

interface SetBrowserIsDetected extends Action {
	type: typeof SET_BROWSER_IS_DETECTED;
	value: boolean;
}

interface SetLanguageChanged extends Action {
	type: typeof SET_LANGUAGE_CHANGED;
	value: FromStage | null;
}

interface SetCoverChanged extends Action {
	type: typeof SET_COVER_CHANGED;
	value: Lightbox | null;
}

interface SetLoadingModels extends Action {
	type: typeof SET_LOADING_MODELS;
	value: boolean;
}

interface SetLoadingCovers extends Action {
	type: typeof SET_LOADING_COVERS;
	value: boolean;
}

interface SetIsDeeplinked extends Action {
	type: typeof SET_IS_DEEPLINKED;
}

interface SetAssetFolder extends Action {
	type: typeof SET_ASSET_FOLDER;
	name: string | null;
}

interface ShowDebug extends Action {
	type: typeof SHOW_DEBUG;
	value: boolean;
}

interface SetUseIndividualAssets extends Action {
	type: typeof SET_USE_INDIVIDUAL_ASSETS;
	value: boolean;
}

interface SetHasVisitScene extends Action {
	type: typeof SET_HAS_VISIT_SCENE;
	value: boolean;
}

interface SetShowFakeDataForm extends Action {
	type: typeof SET_SHOW_FAKE_DATA_FORM;
	value: boolean;
}

interface SetFakeData extends Action {
	type: typeof SET_FAKE_DATA;
	value: IProduct[] | null;
}

interface SetShowIpexGalleryButton extends Action {
	type: typeof SET_SHOW_IPEX_GALLERY_BUTTON;
	value: boolean;
}

interface SetIdleTime extends Action {
	type: typeof SET_IDLE_TIME;
	value: number;
}

interface setIsInUpptackaPlatform extends Action {
	type: typeof SET_IS_IN_UPPTACKA_PLATFORM;
	value: boolean;
}

interface SetFooterHeight extends Action {
	type: typeof SET_FOOTER_HEIGHT;
	value: number;
}

interface SetHeaderHeight extends Action {
	type: typeof SET_HEADER_HEIGHT;
	value: number;
}

interface SetFromNavigator extends Action {
	type: typeof SET_FROM_NAVIGATOR;
	value: string;
}

interface SetProductVariantsShowMore extends Action {
	type: typeof SET_PRODUCT_VARIANTS_SHOW_MORE;
	value: boolean;
}

interface SetProductCardTypeClick extends Action {
	type: typeof SET_PRODUCT_CARD_TYPE_CLICK;
	value: ProductCardItem;
}

interface SetDisableBulkFetch extends Action {
	type: typeof SET_DISABLE_BULK_FETCH;
	value: boolean;
}

interface SetInsightsInitiated extends Action {
	type: typeof SET_INSIGHTS_INITIATED;
	value: boolean;
}

interface SetUsePersistedConfig extends Action {
	type: typeof SET_USE_PERSISTED_CONFIG;
	value: boolean;
}

interface NoArticlesInPlanner extends Action {
	type: typeof NO_ARTICLES_IN_PLANNER;
}

interface SetThumbnailImageChanged extends Action {
	type: typeof THUMBNAIL_IMAGE_CHANGED;
	value: { imageUrl: string; origin: string };
}

interface SetTotalPriceVoice extends Action {
	type: typeof SET_TOTAL_PRICE_VOICE;
	value: string | null;
}

interface StatisticsQrPlanningEnabled extends Action {
	type: typeof STATISTICS_QR_PLANNING_ENABLED;
}

export type ApplicationStateActions =
	| AppInitAction
	| SetAppView
	| SetCurrentLightbox
	| SetSofaRange
	| InitIframeHandler
	| UpdateIframeHeight
	| ResetIframeHeight
	| SetDefaultApplication
	| SuccessAddToBag
	| SuccessAddToBagYmal
	| DoneSaveVpc
	| SwipeInModuleSwiper
	| SetHasFirstInteractionHappened
	| SetBrowserIsDetected
	| SetLanguageChanged
	| SetCoverChanged
	| SetLoadingModels
	| SetLoadingCovers
	| SetAssetFolder
	| ShowDebug
	| SetUseIndividualAssets
	| SetHasVisitScene
	| SetShowFakeDataForm
	| SetFakeData
	| SetShowIpexGalleryButton
	| SetIdleTime
	| setIsInUpptackaPlatform
	| SetFooterHeight
	| SetHeaderHeight
	| SetFromNavigator
	| SetProductVariantsShowMore
	| SetProductCardTypeClick
	| SetDisableBulkFetch
	| SetInsightsInitiated
	| SetUsePersistedConfig
	| NoArticlesInPlanner
	| SetThumbnailImageChanged
	| SetIsDeeplinked
	| SetTotalPriceVoice
	| StatisticsQrPlanningEnabled;
